import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ReactGA from "react-ga4";

import {
  CategoryName,
  CategoryNameHr,
  ModuleNameHr
} from "../../models";
import { useMakes } from "../../routes/Configuration/hooks";
import { makesToNodes } from "../../routes/Configuration/helpers";
import {
  clearMakeModelInfo,
  getMakeModelInfo,
  selectMakeModelInfo,
  selectMakeModelInfoLoading,
  selectMakeModelInfoNotFound
} from "../../routes/MakeModelInfo/makeModelInfo.ducks";
import HelmetComponent from "../../utils/HelmetHelper";
import { Loading } from "../../components/Loading";
import { InvalidPage } from "../../components/InvalidPage";
import BreadcrumbBar from "../../components/BreadcrumbBar";
import { getIconUrl, getImageLink } from "../../services/asset";
import { buildBaseUrl } from "../../services/utils";
import { EngineSpec, SafetyInfo, SpecItem } from "../../typings/interfaces";
import history from "../../history";

import AvailableAdsWidget from "./AvailableAdsWidget";
import SimilarModelsSection from "./SimilarModelsSection";

import styles from "./makeModelInfoPage.scss";

const HERO_SUBTITLE =
  "Pregled generacije, najčešćih motora, osnovnih karakteristika i stvari na koje treba paziti pri kupnji.";

interface MakeModelInfoPageProps {
  metaData?: {
    title?: string;
    description?: string;
    h1?: string;
  };
}

// ---------------------------------------------------------------------------
//  Section wrapper — icon + title + body. Used by every content section on
//  the page. `variant` colours the title (Prednosti/Nedostaci).
// ---------------------------------------------------------------------------

interface SectionProps {
  iconName: string;
  title: string;
  variant?: string;
  children: React.ReactNode;
}

const Section = (props: SectionProps) => {
  const { iconName, title, variant, children } = props;
  const titleClass =
    variant === "advantage"
      ? `${styles.sectionTitle} ${styles.sectionTitleAdvantage}`
      : variant === "drawback"
      ? `${styles.sectionTitle} ${styles.sectionTitleDrawback}`
      : styles.sectionTitle;
  return (
    <section className={styles.section}>
      <header className={styles.sectionHeader}>
        <span className={styles.sectionIcon}>
          <img src={getIconUrl(iconName)} alt="" />
        </span>
        <h2 className={titleClass}>{title}</h2>
      </header>
      <div>{children}</div>
    </section>
  );
};

// ---------------------------------------------------------------------------
//  Small body renderers
// ---------------------------------------------------------------------------

const RichBody = (props: { html: string }) => {
  if (!props.html) return null;
  return (
    <div
      className={styles.richContent}
      dangerouslySetInnerHTML={{ __html: props.html }}
    />
  );
};

const PointsBody = (props: { variant: string; items: string[] }) => {
  const { items } = props;
  if (!items || items.length === 0) return null;
  const marker = props.variant === "advantage" ? "✓" : "—";
  return (
    <ul className={styles.pointsList}>
      {items.map((item, i) => (
        <li key={i} className={styles.pointsItem}>
          <span className={styles.pointsMarker}>{marker}</span>
          <span className={styles.pointsText}>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const EnginesBody = (props: { engines: EngineSpec[] }) => {
  const { engines } = props;
  if (!engines || engines.length === 0) return null;
  return (
    <React.Fragment>
      <table className={styles.enginesTable}>
        <thead>
          <tr>
            <th>Motor</th>
            <th>Gorivo</th>
            <th>Snaga</th>
            <th>Komentar</th>
          </tr>
        </thead>
        <tbody>
          {engines.map((e, i) => (
            <tr key={i}>
              <td>{e.name}</td>
              <td>{e.fuel}</td>
              <td>{e.power}</td>
              <td>{e.comment}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.enginesMobileList}>
        {engines.map((e, i) => (
          <div key={i} className={styles.engineCard}>
            <div className={styles.engineHeadline}>
              <span className={styles.engineName}>{e.name}</span>
              {e.fuel  ? <span>{e.fuel}</span> : null}
              {e.power ? <span>· {e.power}</span> : null}
            </div>
            {e.comment ? (
              <div className={styles.engineComment}>{e.comment}</div>
            ) : null}
          </div>
        ))}
      </div>
    </React.Fragment>
  );
};

const SpecsBody = (props: { items: SpecItem[] }) => {
  const { items } = props;
  if (!items || items.length === 0) return null;
  return (
    <table className={styles.specsTable}>
      <tbody>
        {items.map((item, i) => (
          <tr key={i}>
            <th scope="row">{item.label}</th>
            <td>{item.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SafetyBody = (props: { safety: SafetyInfo; makeName: string; modelName: string }) => {
  const { safety, makeName, modelName } = props;
  if (!safety) return null;
  const buttonLabel = `Pogledaj Euro NCAP test za ${makeName} ${modelName}`;

  const handleSafetyClick = () => {
    ReactGA.event({
      category: "Informacije o modelu",
      action: "Click",
      label: "Pogledan test sigurnosti"
    });
  };

  return (
    <div className={styles.safetyWrapper}>
      {safety.euroNcapUrl ? (
        <a
          className={styles.safetyButton}
          href={safety.euroNcapUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleSafetyClick}
        >
          <img
            className={styles.safetyButtonIcon}
            src={getIconUrl("external-link.svg")}
            alt=""
          />
          <span>{buttonLabel}</span>
        </a>
      ) : (
        <p className={styles.safetyEmpty}>Trenutno nema podataka</p>
      )}
      {safety.text ? <p className={styles.safetyNote}>{safety.text}</p> : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
//  Route → id lookup (make + model slugs → ids via car makes datasource)
// ---------------------------------------------------------------------------

const findIdsFromSlugs = (resources: any, makeSlug: string, modelSlug: string) => {
  if (!resources || !resources.children) {
    return { makeId: null, modelId: null };
  }
  for (let i = 0; i < resources.children.length; i++) {
    const make = resources.children[i];
    if (make.route === makeSlug) {
      if (!make.children) {
        return { makeId: make.id, modelId: null };
      }
      for (let j = 0; j < make.children.length; j++) {
        if (make.children[j].route === modelSlug) {
          return { makeId: make.id, modelId: make.children[j].id };
        }
      }
      return { makeId: make.id, modelId: null };
    }
  }
  return { makeId: null, modelId: null };
};

// ---------------------------------------------------------------------------
//  Page
// ---------------------------------------------------------------------------

const MakeModelInfoPage = (props: MakeModelInfoPageProps) => {
  const { metaData } = props;
  const params: any = useParams();
  const dispatch = useDispatch();

  // Car makes with models, in the { children: [make { children: [model] }] }
  // shape the slug lookup and SimilarModelsSection work on
  const makes = useMakes(CategoryName.car);
  const resources = useMemo(
    () => (makes ? { children: makesToNodes(makes) } : null),
    [makes]
  );
  const makeModelInfo = useSelector(selectMakeModelInfo);
  const loading = useSelector(selectMakeModelInfoLoading);
  const notFound = useSelector(selectMakeModelInfoNotFound);

  useEffect(() => {
    return () => {
      dispatch(clearMakeModelInfo());
    };
  }, []);

  const ids = useMemo(
    () => findIdsFromSlugs(resources, params.makeSlug, params.modelSlug),
    [resources, params.makeSlug, params.modelSlug]
  );

  useEffect(() => {
    if (ids.makeId && ids.modelId) {
      dispatch(getMakeModelInfo(ids.makeId, ids.modelId));
    }
  }, [ids.makeId, ids.modelId]);

  useEffect(() => {
    if (!makeModelInfo || !makeModelInfo.makeYearFrom || !makeModelInfo.makeYearTo) return;
    const canonicalYearRange = `${makeModelInfo.makeYearFrom}-${makeModelInfo.makeYearTo}`;
    if (params.yearRange !== canonicalYearRange) {
      const canonicalPath = `/${ModuleNameHr.vehicles}/${CategoryNameHr.car}/${params.makeSlug}/${params.modelSlug}/${canonicalYearRange}/info`;
      history.replace(canonicalPath);
    }
  }, [makeModelInfo, params.makeSlug, params.modelSlug, params.yearRange]);

  if (params.category !== CategoryNameHr.car) {
    return <InvalidPage />;
  }
  if (!resources || !resources.children) {
    return <Loading />;
  }
  if (!ids.makeId || !ids.modelId) {
    return <InvalidPage />;
  }
  if (loading || (!makeModelInfo && !notFound)) {
    return <Loading />;
  }
  if (notFound || !makeModelInfo) {
    return <InvalidPage />;
  }

  // Prefer admin-entered title; fall back to auto-generated make + model + year
  // range so publishers can skip the field if the default line is enough.
  const autoTitle = `${makeModelInfo.makeName} ${makeModelInfo.modelName} (${makeModelInfo.makeYearFrom}–${makeModelInfo.makeYearTo})`;
  const titleLine = (makeModelInfo.title && makeModelInfo.title.trim()) || autoTitle;
  const seoTitle = (metaData && metaData.title) || `${titleLine} – Index Oglasi`;
  const seoDesc =
    (metaData && metaData.description) ||
    `Pogledajte osnovne informacije o modelu ${makeModelInfo.makeName} ${makeModelInfo.modelName}, najčešće motore, prednosti, nedostatke i savjete za kupnju rabljenog automobila.`;

  const hasAdvantages = makeModelInfo.advantages && makeModelInfo.advantages.length > 0;
  const hasDrawbacks  = makeModelInfo.drawbacks  && makeModelInfo.drawbacks.length > 0;
  const hasEngines    = makeModelInfo.engines    && makeModelInfo.engines.length > 0;
  const hasSpecs      = makeModelInfo.specifications && makeModelInfo.specifications.length > 0;
  const hasSafety     = makeModelInfo.safety && (makeModelInfo.safety.text || makeModelInfo.safety.euroNcapUrl);

  // Prefer the admin-uploaded hero so Discover/social previews show a
  // real car image instead of the default logo. Canonical uses the
  // already-computed canonicalPath pattern so refresh/share both land
  // on the same URL regardless of the yearRange slug the user hit.
  const canonicalPath = `/${ModuleNameHr.vehicles}/${CategoryNameHr.car}/${params.makeSlug}/${params.modelSlug}/${makeModelInfo.makeYearFrom}-${makeModelInfo.makeYearTo}/info`;
  const canonical = buildBaseUrl(canonicalPath);
  const ogImage = makeModelInfo.imageName ? getImageLink(makeModelInfo.imageName, "fb") : undefined;

  return (
    <React.Fragment>
      <HelmetComponent
        title={seoTitle}
        desc={seoDesc}
        canonical={canonical}
        ogImage={ogImage}
        ogImageWidth={ogImage ? 1200 : undefined}
        ogImageHeight={ogImage ? 630 : undefined}
        ogType="article"
      />
      <BreadcrumbBar
        className={styles.breadcrumb}
        routeLinks={[{ title: titleLine }]}
        category={params.category}
      />
      <div className={styles.wrapper}>
        <div className={styles.pageHeading}>
          <h1 className={styles.pageTitle}>{titleLine}</h1>
          <p className={styles.pageSubtitle}>{HERO_SUBTITLE}</p>
        </div>

        <div className={styles.mainCard}>
          {makeModelInfo.imageName ? (
            <div className={styles.heroImage}>
              <img src={getImageLink(makeModelInfo.imageName)} alt={titleLine} />
            </div>
          ) : null}

          {makeModelInfo.overview ? (
            <Section iconName="section-overview.svg" title="Osnovne informacije o modelu">
              <RichBody html={makeModelInfo.overview} />
            </Section>
          ) : null}

          {(hasAdvantages || hasDrawbacks) ? (
            <div className={styles.twoColRow}>
              {hasAdvantages ? (
                <Section
                  iconName="section-advantages.svg"
                  title="Prednosti modela"
                  variant="advantage"
                >
                  <PointsBody variant="advantage" items={makeModelInfo.advantages} />
                </Section>
              ) : null}
              {hasDrawbacks ? (
                <Section
                  iconName="section-drawbacks.svg"
                  title="Mogući nedostaci"
                  variant="drawback"
                >
                  <PointsBody variant="drawback" items={makeModelInfo.drawbacks} />
                </Section>
              ) : null}
            </div>
          ) : null}

          {makeModelInfo.targetBuyer ? (
            <Section iconName="section-target-buyer.svg" title="Za koga je ovaj model dobar izbor?">
              <RichBody html={makeModelInfo.targetBuyer} />
            </Section>
          ) : null}

          {hasEngines ? (
            <Section iconName="section-engines.svg" title="Najčešći motori u ponudi">
              <EnginesBody engines={makeModelInfo.engines} />
            </Section>
          ) : null}

          {hasSpecs ? (
            <Section iconName="section-specifications.svg" title="Osnovne karakteristike">
              <SpecsBody items={makeModelInfo.specifications} />
            </Section>
          ) : null}

          {makeModelInfo.buyingTips ? (
            <Section iconName="section-buying-tips.svg" title="Na što paziti pri kupnji?">
              <RichBody html={makeModelInfo.buyingTips} />
            </Section>
          ) : null}

          {hasSafety ? (
            <Section iconName="section-safety.svg" title="Sigurnost">
              <SafetyBody
                safety={makeModelInfo.safety}
                makeName={makeModelInfo.makeName}
                modelName={makeModelInfo.modelName}
              />
            </Section>
          ) : null}
        </div>

        <AvailableAdsWidget
          makeId={makeModelInfo.makeId}
          modelId={makeModelInfo.modelId}
          variants={makeModelInfo.variants}
        />
        <SimilarModelsSection
          resources={resources}
          similarModels={makeModelInfo.similarModels}
          currentMakeName={makeModelInfo.makeName}
          currentModelName={makeModelInfo.modelName}
        />
      </div>
    </React.Fragment>
  );
};

export default MakeModelInfoPage;



// WEBPACK FOOTER //
// ./src/containers/MakeModelInfoPage/index.tsx